<template>
  <div class="go-dashboard">
    <div class="tms-dashboard__panel-header row">
      <div class="tms-dashboard__panel-container-without-button">
        <HKLLeftRight>
          <div class="tms-dashboard__panel-title-box">
            <div class="tms-dashboard__panel-title">
              {{ $t('tenancy.greenOperation.sustainabilityPartnershipProgramme') }}
              <i
                class="fa fa-refresh dark flat icon_refresh"
                @click="refresh"
              />
            </div>
          </div>
          <div class="tms-dashboard__panel-view-all">
            <HKLRouterLink
              :to="viewAllLinkParam"
              class="none"
            >
              {{ $t('viewAll') }} >
            </HKLRouterLink>
          </div>
        </HKLLeftRight>
      </div>
      <HKLButton
        color="darkblue"
        action="reset"
        @click="reset"
      />
    </div>
    <HKLTable
      ref="tableRef"
      class="non-margin"
      :columns="cols"
      :dataSource="sortedData"
      @sort="watchSorting"
    />
  </div>
</template>

<script>
import lodash from 'lodash'
import { computed, onMounted, ref, unref } from 'vue-demi'
import { getGreenOperationDashboard } from '@/api/tenancies/GreenOperation.request'
import HKLDocBadgeVue from '@/controls/Badge/HKLDocBadge.vue'
import HKLPinBadgeVue from '@/controls/Badge/HKLPinBadge.vue'
import HKLButton from '@/controls/Button/HKLButton.vue'
import HKLLeftRight from '@/controls/Layout/HKLLeftRight.vue'
import HKLText from '@/controls/HKLText.vue'
import HKLRouterLink from '@/controls/Link/HKLRouterLink.vue'
import HKLTable from '@/controls/Table/HKLTable.vue'
import { ccdRouteNames } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import { orderItemByGroup } from '@/utils/sortingHelper'
import useRegionRef from '@/utils/useRegionRef'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { GREEN_OPERATION_SPP_REVIEW_STATUS } from '@/modules/ccd/tenancy/greenOperation/consts'
import { GREEN_OPERATION_SPP_STATUS_OPTIONS } from './AllTenancyRecords.consts'

const sortingCriteria = useRegionRef([], { logoutReset: true })

export default {
  components: {
    HKLButton,
    HKLTable,
    HKLLeftRight,
    HKLRouterLink,
  },
  setup () {
    const hasSubmitGreenOperationAccess = computed(() => {
      return aclHelper.getAccess(ccdPermsTypes.tenancy.submitOperationDoc)
    })
    const viewAllLinkParam = computed(() => ({
      path: 'allTenancyRecs',
      query: {
        goStatus: Object.values(GREEN_OPERATION_SPP_STATUS_OPTIONS)
          .map(option => option.val)
          .filter(Boolean),
      },
    }))
    const tableRef = ref(null)
    const cols = [
      {
        dataIndex: 'companyName',
        title: i18n.t('table.header.companyName'),
      },
      {
        dataIndex: 'showPin',
        title: '',
        component: {
          name: (_, source) => {
            return source.showPin ? HKLPinBadgeVue : HKLText
          },
          attrs: () => {
            return { text: '' }
          },
        },
      },
      {
        dataIndex: 'tradeName',
        title: i18n.t('table.header.tradeName'),
        emptyValue: i18n.t('emptyValue'),
        onSorter: ([_, order], __, list) => {
          return lodash.orderBy(list, ['raw.tradeName'], order)
        },
        component: {
          name: (_, source) => {
            return source.raw.showDoc ? HKLDocBadgeVue : HKLRouterLink
          },
          attrs: (value, source) => {
            const attrs = { text: value || i18n.t('emptyValue') }
            if (source) {
              const to = {
                name: ccdRouteNames.ccdTenancyView,
                query: { tabIndex: 3 },
                params: { id: source.raw.tenancy.id },
              }
              Object.assign(attrs, {
                to,
                component: HKLRouterLink,
                class: 'txt-right-10',
              })
            }
            return attrs
          },
        },
      },
      {
        dataIndex: 'building',
        title: i18n.t('table.header.building'),
      },
      {
        dataIndex: 'displayStatus',
        title: i18n.t('table.header.status'),
      },
      {
        dataIndex: 'numberOfRecognition',
        title: i18n.t('tenancy.greenOperation.numberOfRecognition'),
      },
      {
        dataIndex: 'expiredOn',
        title: i18n.t('table.header.expiredOn'),
        onSorter: ([_, order], __, list) => {
          return lodash.orderBy(list, ['raw.sppExpiredDate'], order)
        },
      },
    ]
    const rawData = ref([])
    const refresh = async () => {
      rawData.value = await getGreenOperationDashboard()
    }
    const parsedData = computed(() => {
      return unref(rawData).map(item => {
        const status = lodash.get(item, 'sppReviewStatus') || GREEN_OPERATION_SPP_REVIEW_STATUS.NEW
        const sppExpiredDate = lodash.get(item, 'sppExpiredDate')
        const expiredOn = dateUtil.formatByLongDate(sppExpiredDate) || i18n.t('emptyValue')
        const showDoc = unref(hasSubmitGreenOperationAccess) && item.showDoc
        return {
          companyName: lodash.get(item, 'tenancy.company.name'),
          tradeName: lodash.get(item, 'tenancy.tradeName'),
          building: lodash.get(item, 'tenancy.building.code'),
          displayStatus: i18n.t(`status.${status.toLowerCase()}`).toUpperCase(),
          expiredOn,
          numberOfRecognition: 0,
          ...item,
          showDoc,
        }
      })
    })
    const sortedData = computed(() => {
      return lodash.orderBy(
        unref(parsedData),
        [
          (item) => orderItemByGroup(item, ['showPin', 'showDoc']),
          'tradeName',
        ],
        ['asc', 'asc'],
      )
    })
    const restoreSorting = () => {
      unref(tableRef).forceUpdateSortObject(sortingCriteria.value)
    }
    const reset = () => {
      unref(tableRef).forceUpdateSortObject([])
    }
    onMounted(async () => {
      await refresh()
      restoreSorting()
    })
    return {
      viewAllLinkParam,
      sortingCriteria,
      cols,
      parsedData,
      sortedData,
      refresh,
      reset,
      tableRef,
      watchSorting: (val) => {
        sortingCriteria.value = val
      },
    }
  },
}
</script>

<style lang="scss" scoped>
@import '../../assets/styles/dashboard/tenant.scss';

.go-dashboard {
  ::v-deep .hkl-table {
    .ant-table-tbody > tr > td.show-pin {
      padding: 0;
    }
    .ant-table-column-title {
      white-space: normal;
  }
    .ant-table-tbody > tr > td.trade-name,
    .ant-table-thead > tr > th.trade-name {
      padding-left: 0;
    }
    th.company-name {
      width: 280px;
    }
    th.building {
      width: 145px;
    }
    th.display-status {
      width: 120px;
    }
    th.number-of-recognition {
      width: 185px;
    }
    th.expired-on {
      width: 155px;
    }
  }

}
</style>
